import type {
	ParametricListValue,
	ParametricListValuewithCount
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import { ColorConfiguratorModes } from "~/utils/enums";
import { variantImageService } from "~/layers/base/domain/product/variant-image.service";

/**
 * Composable for reactive variants color image URL generation
 */
export const useColorImage = () => {
	const { products, selectedSecondParametric } = storeToRefs(useProductStore());
	const config = useRuntimeConfig();

	const shouldShowImage = (
		color: ParametricListValue | ParametricListValuewithCount
	): boolean => {
		return color.Description === ColorConfiguratorModes.IMAGE;
	};

	/**
	 * URL of the image uploaded on the parametric *value* itself (Norce admin →
	 * Parametrics → value → Image). Empty string when the value has no image, so
	 * callers fall back to the flat colour swatch. Unlike `getColorImageUrl` this
	 * never falls back to a product/variant image — a pattern swatch ("Striped")
	 * must show the pattern, not the garment.
	 */
	const getColorSwatchImageUrl = (
		color: ParametricListValue | ParametricListValuewithCount,
		imageQueryParams: string = "?format=webp&w=64"
	): string => {
		return color.ImageKey
			? `${config.public.norce.cdn}/${color.ImageKey}${imageQueryParams}`
			: "";
	};

	const getColorImageUrl = (
		color: ParametricListValue | ParametricListValuewithCount,
		imageQueryParams: string = "?format=webp&w=80"
	): string => {
		if (color.ImageKey) {
			return `${config.public.norce.cdn}/${color.ImageKey}${imageQueryParams}`;
		}

		if (!products.value) {
			return "";
		}

		const variantImageKey = variantImageService.findVariantImageKey({
			product: products.value,
			color,
			selectedSecondParametric: selectedSecondParametric.value
		});

		if (variantImageKey) {
			return `${config.public.norce.cdn}/${variantImageKey}${imageQueryParams}`;
		}

		return "";
	};

	return {
		shouldShowImage,
		getColorImageUrl,
		getColorSwatchImageUrl
	};
};
